
import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, Calendar, Building, ChevronDown, ChevronUp, ArrowDown, ArrowUp } from 'lucide-react';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Button } from './ui/button';

const ExperienceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Record<string, boolean>>({
    'job1': false,
    'job2': false
  });
  const sectionRef = useRef<HTMLElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleJob = (jobId: string) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  return (
    <section id="experience" className="bg-secondary/20 relative" ref={sectionRef}>
      <div className="experience-vector experience-vector-1"></div>
      <div className="experience-vector experience-vector-2"></div>
      
      <div className="section-container relative z-10">
        <div className={`mb-8 sm:mb-12 transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0 fade-up' : 'opacity-0 translate-y-8'
        }`}>
          <p className="heading-highlight mb-2 animate-pulse-slow">My Journey</p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Professional Experience</h2>
          <p className="text-muted-foreground max-w-2xl text-sm sm:text-base">
            With 5+ years of industry experience, I've contributed to various projects
            across different domains and technologies.
          </p>
        </div>
        
        <div className="space-y-6 sm:space-y-10">
          {/* First job */}
          <div className={`bg-card border border-border/50 rounded-lg p-5 sm:p-6 hover:border-accent/50 transition-all duration-300 ${
            isVisible ? 'opacity-100 translate-y-0 slide-right' : 'opacity-0 translate-y-8'
          }`} style={{ transitionDelay: '150ms' }}>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building className="h-4 w-4 text-gray-400" />
                  <h3 className="text-lg sm:text-xl font-semibold">Interface Infosoft Solutions</h3>
                </div>
                <p className="text-muted-foreground text-sm">Software Engineer</p>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>May 2023 - Present</span>
              </div>
            </div>
            
            {/* Preview of job details - showing exactly 2 lines */}
            <div className="text-sm text-muted-foreground mb-4">
              <ul className="space-y-2">
                <li className="flex gap-2">
                  <span className="text-primary text-lg leading-5">•</span>
                  <span>Built a micro-frontend medical data platform from scratch using React 18, TypeScript, Vite, and Module Federation.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary text-lg leading-5">•</span>
                  <span>Led a team of three frontend engineers, establishing team standards for strict TypeScript usage, SonarQube analysis, and automated test coverage.</span>
                </li>
              </ul>
            </div>
            
            <Collapsible open={expandedJobs['job1']} onOpenChange={() => toggleJob('job1')}>
              <CollapsibleTrigger className="w-full">
                <Button 
                  variant="outline" 
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-primary/20 to-accent/20 hover:from-primary/40 hover:to-accent/40 border border-primary/30 hover:border-primary/60 text-primary/90 hover:text-primary font-medium backdrop-blur-sm transition-all duration-300 shadow-sm hover:shadow-md rounded-md group"
                >
                  <span className="transition-all duration-300">
                    {expandedJobs['job1'] ? 'Hide details' : 'View complete experience'}
                  </span>
                  <div className="relative h-5 w-5">
                    <ArrowDown className={`h-5 w-5 absolute top-0 left-0 transition-all duration-500 ${expandedJobs['job1'] ? 'opacity-0 -translate-y-2' : 'opacity-100 animate-bounce translate-y-0'}`} />
                    <ArrowUp className={`h-5 w-5 absolute top-0 left-0 transition-all duration-500 ${expandedJobs['job1'] ? 'opacity-100 animate-bounce-down translate-y-0' : 'opacity-0 translate-y-2'}`} />
                  </div>
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="pt-6 space-y-5 overflow-hidden transition-all duration-500 ease-in-out">
                <div className="animate-fade-in">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Enterprise Platform</Badge>
                    <h4 className="font-medium">Medical Data Management System | US Client | May'24-Present</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Built a micro-frontend platform using React, TypeScript, and Module Federation to decouple the host portal from 3 specialized viewer remotes.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Implemented data grid virtualization with React Virtuoso, resolving browser memory limits when rendering 1,000+ medical imaging studies.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Integrated an in-browser DICOM annotation viewer with backend APIs for automated data de-identification, ensuring patient data remained secure during review.</span>
                    </li>
                     <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Helped establish team standards around strict TypeScript usage, full internationalization (i18next), SonarQube analysis, and automated testing (Vitest, RTL).</span>
                    </li>
                  </ul>
                </div>
                
                <Separator className="bg-border/40" />
                
                <div className="animate-fade-in" style={{ animationDelay: '50ms' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Product Engineering</Badge>
                    <h4 className="font-medium">Health & Wellness Engagement Platform | In-house Product | Dec'23-Present</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Led a team of three frontend engineers, driving feature delivery, code reviews, UI consistency, and technical mentoring.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Collaborated closely with product stakeholders to deliver a wellness event management platform used by 1,000+ users.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Built user dashboards for real-time enrollment tracking, plus an admin console managing 50+ events, challenges, and platform features.</span>
                    </li>
                  </ul>
                </div>
                
                <Separator className="bg-border/40" />
                
                <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Security & Workflow</Badge>
                    <h4 className="font-medium">Medical Device Registration Portal | US Client | Jul'23-Apr'25</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Developed a registration portal enabling secure onboarding of 500+ medical devices, with a custom IDP-based authentication system serving 500+ users.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Built interactive physician dashboards for patient health monitoring, configurable alerting, and in-platform care-team communication.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Implemented role-based access control (RBAC) across 3 distinct roles (Physician, Admin, Super Admin), securing access to critical clinical functionality.</span>
                    </li>
                  </ul>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          
          {/* Second job */}
          <div className={`bg-card border border-border/50 rounded-lg p-5 sm:p-6 hover:border-accent/50 transition-all duration-300 ${
            isVisible ? 'opacity-100 translate-y-0 slide-left' : 'opacity-0 translate-y-8'
          }`} style={{ transitionDelay: '300ms' }}>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building className="h-4 w-4 text-gray-400" />
                  <h3 className="text-lg sm:text-xl font-semibold">Redsoftware Solutions</h3>
                </div>
                <p className="text-muted-foreground text-sm">Software Engineer</p>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>Sep 2021 - Mar 2023</span>
              </div>
            </div>
            
            {/* Preview of job details - showing exactly 2 lines */}
            <div className="text-sm text-muted-foreground mb-4">
              <ul className="space-y-2">
                <li className="flex gap-2">
                  <span className="text-primary text-lg leading-5">•</span>
                  <span>Built an AI-powered chatbot using the OpenAI API, delivered as an embeddable JavaScript widget deployed across 10+ client websites, cutting client setup time by 40%.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary text-lg leading-5">•</span>
                  <span>Engineered cross-chain NFT buy, sell, transfer, and burn flows on Immutable X and Polygon (Layer 2) networks.</span>
                </li>
              </ul>
            </div>
            
            <Collapsible open={expandedJobs['job2']} onOpenChange={() => toggleJob('job2')}>
              <CollapsibleTrigger className="w-full">
                <Button 
                  variant="outline" 
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-primary/20 to-accent/20 hover:from-primary/40 hover:to-accent/40 border border-primary/30 hover:border-primary/60 text-primary/90 hover:text-primary font-medium backdrop-blur-sm transition-all duration-300 shadow-sm hover:shadow-md rounded-md group"
                >
                  <span className="transition-all duration-300">
                    {expandedJobs['job2'] ? 'Hide details' : 'View complete experience'}
                  </span>
                  <div className="relative h-5 w-5">
                    <ArrowDown className={`h-5 w-5 absolute top-0 left-0 transition-all duration-500 ${expandedJobs['job2'] ? 'opacity-0 -translate-y-2' : 'opacity-100 animate-bounce translate-y-0'}`} />
                    <ArrowUp className={`h-5 w-5 absolute top-0 left-0 transition-all duration-500 ${expandedJobs['job2'] ? 'opacity-100 animate-bounce-down translate-y-0' : 'opacity-0 translate-y-2'}`} />
                  </div>
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="pt-6 space-y-5 overflow-hidden transition-all duration-500 ease-in-out">
                <div className="animate-fade-in">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">AI Integration</Badge>
                    <h4 className="font-medium">AI Chatbot Widget | Mar'23-Apr'23</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Built an AI-powered chatbot using the OpenAI API, delivered as an embeddable JavaScript widget deployed across 10+ client websites via a single script-tag integration.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Cut client setup time by 40% and significantly reduced user inquiry response time.</span>
                    </li>
                  </ul>
                </div>
                
                <Separator className="bg-border/40" />
                
                <div className="animate-fade-in" style={{ animationDelay: '50ms' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Web3 / Blockchain</Badge>
                    <h4 className="font-medium">NFT-Marketplace | Apr'22-Mar'23</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Engineered cross-chain NFT buy, sell, transfer, and burn flows on Immutable X and Polygon (Layer 2) networks.</span>
                    </li>
                  </ul>
                </div>
                
                <Separator className="bg-border/40" />
                
                <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Metaverse</Badge>
                    <h4 className="font-medium">Metaverse-Scenes | Feb'22-Jan'23</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Designed and built interactive 3D scenes on Decentraland, The Sandbox, and Roblox Studio.</span>
                    </li>
                  </ul>
                </div>
                
                <Separator className="bg-border/40" />
                
                <div className="animate-fade-in" style={{ animationDelay: '150ms' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs bg-primary/5">Full-Stack</Badge>
                    <h4 className="font-medium">File-Management System | Oct'21-Jan'22</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Implemented OTP-based secure access control for 200+ shared files/folders serving 100+ users.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary text-lg leading-5">•</span>
                      <span>Built full-stack architecture using Next.js, Nest.js, MongoDB, and Node.js.</span>
                    </li>
                  </ul>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
